import React, { Fragment, useContext, useState, useEffect, forwardRef } from 'react';
import cn from 'clsx';
import { string, oneOfType, number, func, arrayOf, shape } from 'prop-types';
import { paramCase } from 'change-case';
import ScrollableContainer from '../../atoms/ScrollableContainer/index.jsx';
import { useRefs } from '../../hooks/index.js';
import FormContext from '../../context/FormContext.js';
import FormGroupContext from '../../context/FormGroupContext.js';

/**
 * FormRadioTabs component.
 */
const FormRadioTabs = /*#__PURE__*/forwardRef(({
  className,
  defaultValue,
  legend,
  name,
  onChange,
  options,
  srOnlyLegend,
  value
}, ref) => {
  const {
    inputId,
    feedbackId
  } = useContext(FormGroupContext);
  const {
    inverted,
    onChange: onFormChange,
    defaultValues
  } = useContext(FormContext);
  const inputRefs = useRefs(options || []);
  const [currentValue, setCurrentValue] = useState(value || defaultValue || (defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues[name]));
  const activeOption = options === null || options === void 0 ? void 0 : options.find(option => option.value === currentValue);
  useEffect(() => {
    if (value && value !== currentValue) {
      setCurrentValue(value);
    }
  }, [value]);

  /**
   * Handle a radio button being clicked.
   */
  const handleChange = evt => {
    setCurrentValue(evt.target.value);
    if (onChange) {
      onChange(evt);
    }
    if (onFormChange) {
      onFormChange(evt);
    }
  };

  /**
   * Get the React ref for an option.
   */
  const getInputRef = checkedOption => {
    const optionIndex = options.findIndex(option => option === checkedOption);
    return inputRefs[optionIndex];
  };

  /**
   * Select the first option in a group.
   */
  const selectFirstOptionInGroup = optionGroup => {
    const option = options.find(({
      group
    }) => group === optionGroup);
    setCurrentValue(option.value);
    getInputRef(option).current.click();
  };

  /**
   * Check if an option is active.
   */
  const isOptionActive = option => option.value === currentValue;

  /**
   * Get a DOM ID for an element.
   */
  const getDomId = suffix => paramCase([inputId, suffix].filter(x => x).join('-'));
  return /*#__PURE__*/React.createElement("fieldset", {
    className: cn('form-radio-tabs', className, (activeOption === null || activeOption === void 0 ? void 0 : activeOption.group) && 'form-radio-tabs--has-active-group', inverted && 'form-radio-tabs--inverted'),
    ref: ref
  }, (legend || srOnlyLegend) && /*#__PURE__*/React.createElement("legend", {
    className: 'form-radio-tabs__legend body-copy mb-xs' + (srOnlyLegend ? " sr-only" : "")
  }, legend || srOnlyLegend), /*#__PURE__*/React.createElement(ScrollableContainer, null, /*#__PURE__*/React.createElement("div", {
    className: "d-flex"
  }, options === null || options === void 0 ? void 0 : options.map((option, i) => {
    const previousOptions = options.slice(0, i);
    const id = getDomId(option.group || option.value);
    const isActiveGroup = option.group === (activeOption === null || activeOption === void 0 ? void 0 : activeOption.group);
    const optionsInGroup = options.filter(({
      group
    }) => group === option.group);

    // Only add the first option of each group to the top-level tabs.
    const hadGroup = option.group && previousOptions.find(({
      group
    }) => group === option.group);
    if (hadGroup) {
      return null;
    }
    return /*#__PURE__*/React.createElement(Fragment, {
      key: id
    }, /*#__PURE__*/React.createElement("div", {
      className: "form-radio-tabs__tab"
    }, option.group ? /*#__PURE__*/React.createElement("button", {
      className: 'form-radio-tabs__tab-label' + ((activeOption === null || activeOption === void 0 ? void 0 : activeOption.group) === option.group ? " form-radio-tabs__tab-label--active" : ""),
      type: "button",
      tabIndex: "-1",
      onClick: () => {
        selectFirstOptionInGroup(option.group);
      }
    }, option.group) : /*#__PURE__*/React.createElement("label", {
      className: "form-radio-tabs__tab-label mb-reset d-flex justify-content-center" + (isOptionActive(option) ? " form-radio-tabs__tab-label--active" : ""),
      htmlFor: id
    }, option.label, /*#__PURE__*/React.createElement("input", {
      className: "sr-only",
      type: "radio",
      id: id,
      value: option.value,
      name: name,
      checked: isOptionActive(option),
      onChange: handleChange,
      ref: getInputRef(option),
      "aria-describedby": feedbackId
    }))), option.group && /*#__PURE__*/React.createElement(ScrollableContainer, {
      className: "form-radio-tabs__group p-absolute d-flex " + (isActiveGroup ? 'form-radio-tabs__group--active' : 'sr-only')
    }, /*#__PURE__*/React.createElement("div", {
      className: "d-flex"
    }, optionsInGroup.map(groupedOption => {
      const groupedOptionInputId = getDomId(groupedOption.value);
      return /*#__PURE__*/React.createElement("label", {
        className: "form-radio-tabs__grouped-radio-button-label mr-md mb-reset",
        htmlFor: groupedOptionInputId,
        key: groupedOptionInputId
      }, /*#__PURE__*/React.createElement("input", {
        type: "radio",
        className: "form-radio-tabs__grouped-radio-button-input mr-xxs mb-reset",
        id: groupedOptionInputId,
        value: groupedOption.value,
        name: name,
        checked: isOptionActive(groupedOption),
        onChange: handleChange,
        ref: getInputRef(groupedOption),
        "aria-describedby": feedbackId
      }), groupedOption.label);
    }))));
  }))));
});
FormRadioTabs.displayName = 'FormRadioTabs';
FormRadioTabs.defaultProps = {
  className: null,
  defaultValue: null,
  legend: null,
  name: null,
  onChange: null,
  options: [],
  srOnlyLegend: null,
  value: null
};
FormRadioTabs.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** The default value for the input field. */
  defaultValue: string,
  /** A legend to show above the buttons. */
  legend: string,
  /** The input name. */
  name: oneOfType([number, string]),
  /** On change event handler. */
  onChange: func,
  /** The radio button options. */
  options: arrayOf(shape({
    group: string,
    label: string,
    value: string
  })),
  /** Add a legend for screen readers only. */
  srOnlyLegend: string,
  /** The value for the input field. */
  value: string
} : {};
export default FormRadioTabs;